import { MedplumClient } from "@medplum/core";
import {
  ChargeItemDefinition,
  CodeSystem,
  CodeSystemConcept,
  Practitioner,
  Reference,
  ValueSet,
} from "@medplum/fhirtypes";

import { getProcedureFeeChargeItemDefinitionUrl } from "@/fhir/charge-item-definition";
import { CUSTOM_TAG } from "@/fhir/constants/tags";
import { setAccountsBestEffort } from "@/utils/accounts";
import { getOrganizationReference, joinReferenceString } from "@/utils/medplum";

import {
  CUSTOM_PROCEDURE_CODE_SYSTEM,
  CUSTOM_PROCEDURE_VALUE_SET_BASE_URL,
} from "../constants/custom-codes";
import { ProcedureCode } from "../types/fee-table";
import { notifyCombinedProcedureCodesRebuildFailed } from "../utils/notify-combined-procedure-codes-rebuild-failed";
import { CustomCodeFormType } from "../zod-schemas/custom-code";

import { scheduleCombinedProcedureCodesValueSetRebuild } from "./combined-procedure-codes";
import {
  fetchProcedureFeeQuestionnaire,
  fetchProcedureFeeQuestionnaireResponse,
  fetchProcedureFeeResponsesByCode,
  parseProcedureFeeQuestionnaireResponse,
  saveProcedureFeeQuestionnaireResponse,
} from "./procedure-fees";

/**
 * Gets the practice-specific ValueSet URL for custom codes
 */
export function getCustomCodeValueSetUrl(practiceId: string): string {
  return `${CUSTOM_PROCEDURE_VALUE_SET_BASE_URL}/${practiceId}`;
}

/**
 * Gets the practice-specific CodeSystem URL for custom codes
 * For now, we'll use a single CodeSystem per practice
 */
export function getCustomCodeSystemUrl(practiceId: string): string {
  return `${CUSTOM_PROCEDURE_CODE_SYSTEM}/${practiceId}`;
}

/**
 * Loads custom procedure codes from a practice-scoped CodeSystem
 */
export async function loadCustomProcedureCodes(
  medplum: MedplumClient,
  practiceId: string,
): Promise<ProcedureCode[]> {
  try {
    const codeSystemUrl = getCustomCodeSystemUrl(practiceId);
    const organizationReference = getOrganizationReference(practiceId);

    // Search for the practice's custom CodeSystem
    const codeSystem = await medplum.searchOne("CodeSystem", {
      url: codeSystemUrl,
      _compartment: organizationReference,
    });

    if (!codeSystem || !codeSystem.concept) {
      return [];
    }

    // Map CodeSystem concepts to ProcedureCode format
    const procedureCodes: ProcedureCode[] = codeSystem.concept.map((concept) => ({
      code: concept.code ?? "",
      name: concept.display ?? "",
      information: concept.definition ?? "",
      isActive: true, // Default, will be overridden by QuestionnaireResponse if exists
      insuranceFee: null,
      selfPayFee: null,
      coding: {
        system: codeSystemUrl,
        code: concept.code ?? "",
        display: concept.display ?? "",
      },
    }));

    // Load fee data from QuestionnaireResponses
    const questionnaire = await fetchProcedureFeeQuestionnaire(medplum);
    if (questionnaire) {
      const profile = await medplum.getProfile();
      const practitionerReference: Reference<Practitioner> | undefined =
        profile?.resourceType === "Practitioner" && profile.id
          ? { reference: joinReferenceString("Practitioner", profile.id) }
          : undefined;

      if (organizationReference || practitionerReference) {
        const responsesByCode = await fetchProcedureFeeResponsesByCode(
          medplum,
          questionnaire,
          practitionerReference,
          organizationReference,
        );

        // Merge fee data from QuestionnaireResponses
        return procedureCodes.map((procedure) => {
          const response = responsesByCode[procedure.code];
          return parseProcedureFeeQuestionnaireResponse(response, procedure);
        });
      }
    }

    return procedureCodes;
  } catch (error) {
    console.error("Error loading custom procedure codes:", error);
    return [];
  }
}

/**
 * Ensures {@link CUSTOM_TAG} is on `codeSystem.meta.tag` when missing so Medplum access rules
 * such as `CodeSystem?_tag=http://quiltedhealth.com|custom` allow `update` on legacy resources.
 */
function ensureCustomTagOnCodeSystem(codeSystem: CodeSystem): void {
  const existingTags = codeSystem.meta?.tag ?? [];
  const hasCustomTag = existingTags.some(
    (tag) => tag.system === CUSTOM_TAG.system && tag.code === CUSTOM_TAG.code,
  );
  if (hasCustomTag) return;
  codeSystem.meta = { ...codeSystem.meta, tag: [...existingTags, CUSTOM_TAG] };
}

/**
 * Creates or updates a custom procedure code in the practice's CodeSystem
 */
export async function upsertCustomProcedureCode(
  medplum: MedplumClient,
  practiceId: string,
  formValues: CustomCodeFormType,
  existingCode?: string, // If provided, this is an update
): Promise<void> {
  const codeSystemUrl = getCustomCodeSystemUrl(practiceId);
  const organizationReference = getOrganizationReference(practiceId);

  // Search for existing CodeSystem
  let codeSystem = await medplum.searchOne("CodeSystem", {
    url: codeSystemUrl,
    _compartment: organizationReference,
  });

  const concept: CodeSystemConcept = {
    code: formValues.code,
    display: formValues.name,
    definition: formValues.information || "",
  };

  if (codeSystem?.id) {
    // Update existing CodeSystem
    const conceptCountBefore = (codeSystem.concept ?? []).length;
    const concepts = codeSystem.concept || [];

    if (existingCode && existingCode !== formValues.code) {
      // Code was changed - remove old concept and add new one
      const updatedConcepts = concepts.filter((conceptItem) => conceptItem.code !== existingCode);
      updatedConcepts.push(concept);
      codeSystem.concept = updatedConcepts;
    } else {
      // Update existing concept or add new one
      const existingIndex = concepts.findIndex(
        (conceptItem) => conceptItem.code === formValues.code,
      );
      if (existingIndex >= 0) {
        concepts[existingIndex] = concept;
      } else {
        concepts.push(concept);
      }
      codeSystem.concept = concepts;
    }

    codeSystem.status = "active";
    ensureCustomTagOnCodeSystem(codeSystem);
    await medplum.updateResource(codeSystem);

    if (conceptCountBefore === 0) {
      await upsertCustomCodeValueSet(medplum, practiceId, codeSystemUrl);
    }
  } else {
    // Create new CodeSystem
    const newCodeSystem: CodeSystem = {
      resourceType: "CodeSystem",
      url: codeSystemUrl,
      name: "Custom Procedure Codes",
      title: "Custom Procedure Codes",
      status: "active",
      content: "complete",
      concept: [concept],
      meta: {
        tag: [CUSTOM_TAG],
      },
    };

    codeSystem = await medplum.createResource(newCodeSystem);

    await setAccountsBestEffort(medplum, codeSystem, [{ reference: organizationReference }]);

    // Create or update the practice's ValueSet
    await upsertCustomCodeValueSet(medplum, practiceId, codeSystemUrl);
  }

  // Save fees using QuestionnaireResponse (same as non-custom codes)
  const questionnaire = await fetchProcedureFeeQuestionnaire(medplum);
  if (!questionnaire) {
    throw new Error("Procedure fee questionnaire not found");
  }

  const profile = await medplum.getProfile();
  if (profile?.resourceType !== "Practitioner" || !profile.id) {
    throw new Error("Practitioner profile not found");
  }

  const practitionerReference: Reference<Practitioner> = {
    reference: joinReferenceString("Practitioner", profile.id),
  };

  const procedureCode: ProcedureCode = {
    code: formValues.code,
    name: formValues.name,
    information: formValues.information || "",
    isActive: formValues.isActive,
    insuranceFee: formValues.insuranceFee,
    selfPayFee: formValues.selfPayFee,
    coding: {
      system: codeSystemUrl,
      code: formValues.code,
      display: formValues.name,
    },
  };

  const existingResponse = existingCode
    ? await fetchProcedureFeeQuestionnaireResponse(
        medplum,
        existingCode,
        questionnaire,
        undefined,
        organizationReference,
      )
    : await fetchProcedureFeeQuestionnaireResponse(
        medplum,
        formValues.code,
        questionnaire,
        undefined,
        organizationReference,
      );

  await saveProcedureFeeQuestionnaireResponse(
    medplum,
    questionnaire,
    procedureCode,
    {
      insuranceFee: formValues.insuranceFee,
      selfPayFee: formValues.selfPayFee,
      isActive: formValues.isActive,
    },
    practitionerReference,
    practiceId,
    existingResponse,
    practiceId,
  );

  // Invalidate searches to refresh data
  medplum.invalidateSearches("CodeSystem");
  medplum.invalidateSearches("ValueSet");
  medplum.invalidateSearches("QuestionnaireResponse");

  // Full combined ValueSet rebuild expands many fee ValueSets; run in the background so save
  // returns after durable writes (CodeSystem + QuestionnaireResponse) without blocking on rebuild.
  scheduleCombinedProcedureCodesValueSetRebuild(
    medplum,
    practiceId,
    notifyCombinedProcedureCodesRebuildFailed,
  );
}

/**
 * Finds ChargeItemDefinition resources for a canonical `url`, merging unscoped search with an optional `_compartment` search so practice-scoped definitions linked only via Organization compartment are still discoverable (mirrors procedure-fee QuestionnaireResponse search behavior).
 *
 * @param medplum Medplum client
 * @param url Canonical ChargeItemDefinition.url value
 * @param options When `organizationReference` is set, also searches with `_compartment` and deduplicates by resource id
 * @returns Deduplicated matches (at most one page each; `_count` raised to reduce truncation vs default bundle size)
 */
async function findChargeItemDefinitionsForUrl(
  medplum: MedplumClient,
  url: string,
  options?: { organizationReference?: string; maxCount?: number },
): Promise<ChargeItemDefinition[]> {
  const maxCount = options?.maxCount ?? 1000;
  const byId = new Map<string, ChargeItemDefinition>();

  await accumulateChargeItemDefinitionsById(medplum, url, byId, { url, _count: maxCount });
  if (options?.organizationReference) {
    await accumulateChargeItemDefinitionsById(medplum, url, byId, {
      url,
      _count: maxCount,
      _compartment: options.organizationReference,
    });
  }

  return [...byId.values()];
}

async function accumulateChargeItemDefinitionsById(
  medplum: MedplumClient,
  url: string,
  byId: Map<string, ChargeItemDefinition>,
  query: Record<string, string | number>,
): Promise<void> {
  try {
    const matches = await medplum.searchResources("ChargeItemDefinition", query);
    for (const chargeItemDefinition of matches) {
      if (chargeItemDefinition.id) {
        byId.set(chargeItemDefinition.id, chargeItemDefinition);
      }
    }
  } catch (error) {
    console.warn(`Could not search ChargeItemDefinition for url ${url}:`, error);
  }
}

/**
 * Deletes ChargeItemDefinition resources for a canonical `url`.
 * @param allMatching When true, deletes every merged match (practice-scoped URL). When false, deletes at most one (legacy global URL; avoids cross-practice bulk delete).
 */
async function deleteChargeItemDefinitionsMatching(
  medplum: MedplumClient,
  url: string,
  code: string,
  options: { organizationReference?: string; allMatching: boolean },
): Promise<number> {
  try {
    const matches = await findChargeItemDefinitionsForUrl(medplum, url, {
      organizationReference: options.organizationReference,
    });
    const targets = options.allMatching ? matches : matches.slice(0, 1);
    let deleted = 0;
    for (const def of targets) {
      if (!def.id) continue;
      try {
        await medplum.deleteResource("ChargeItemDefinition", def.id);
        deleted++;
      } catch (error) {
        console.warn(`Could not delete ChargeItemDefinition ${def.id} for code ${code}:`, error);
      }
    }
    return deleted;
  } catch (error) {
    console.warn(`Could not delete ChargeItemDefinition for code ${code}:`, error);
    return 0;
  }
}

async function deleteProcedureFeeChargeItemDefinitionForCode(
  medplum: MedplumClient,
  practiceId: string,
  code: string,
): Promise<void> {
  const organizationReference = getOrganizationReference(practiceId);
  const primaryUrl = getProcedureFeeChargeItemDefinitionUrl(code, practiceId);
  const fallbackUrl = getProcedureFeeChargeItemDefinitionUrl(code);

  if (
    (await deleteChargeItemDefinitionsMatching(medplum, primaryUrl, code, {
      organizationReference,
      allMatching: true,
    })) > 0
  ) {
    return;
  }

  if (primaryUrl === fallbackUrl) {
    return;
  }

  await deleteChargeItemDefinitionsMatching(medplum, fallbackUrl, code, {
    allMatching: false,
  });
}

/**
 * Retires the practice custom CodeSystem when the last concept was removed (`concept: []`, `status: retired`).
 * Uses UPDATE only: access policies often allow `update` but forbid `delete` on CodeSystem, avoiding a doomed DELETE (403) in the browser.
 */
async function retireEmptyCustomCodeSystemWhenListCleared(
  medplum: MedplumClient,
  codeSystem: CodeSystem,
): Promise<void> {
  if (!codeSystem.id) {
    throw new Error("CodeSystem id missing");
  }
  ensureCustomTagOnCodeSystem(codeSystem);
  await medplum.updateResource({
    ...codeSystem,
    concept: [],
    status: "retired",
  });
}

/**
 * Marks the practice custom-code ValueSet as retired when the last custom procedure was removed.
 * Uses UPDATE only: access policies often allow `update` but forbid `delete` on ValueSet, which avoids a doomed DELETE (403) in the browser while still clearing the list for the app.
 */
async function retireCustomCodeValueSetWhenListCleared(
  medplum: MedplumClient,
  valueSet: ValueSet,
): Promise<void> {
  if (!valueSet.id) {
    return;
  }
  await medplum.updateResource({
    ...valueSet,
    status: "retired",
  });
}

/**
 * Deletes a custom procedure code from the practice's CodeSystem
 */
export async function deleteCustomProcedureCode(
  medplum: MedplumClient,
  practiceId: string,
  code: string,
): Promise<void> {
  const codeSystemUrl = getCustomCodeSystemUrl(practiceId);
  const organizationReference = getOrganizationReference(practiceId);

  const codeSystem = await medplum.searchOne("CodeSystem", {
    url: codeSystemUrl,
    _compartment: organizationReference,
  });

  if (!codeSystem?.id || !codeSystem.concept) {
    throw new Error("Custom code not found");
  }

  // Check if the code exists in the concept array
  const codeExists = codeSystem.concept.some((conceptItem) => conceptItem.code === code);
  if (!codeExists) {
    throw new Error("Custom code not found");
  }

  // Delete fee artifacts before mutating CodeSystem so ChargeItemDefinitions remain discoverable and subscriptions cannot strand CID rows behind a removed code.
  await deleteProcedureFeeChargeItemDefinitionForCode(medplum, practiceId, code);

  // Also delete the QuestionnaireResponse if it exists
  const questionnaire = await fetchProcedureFeeQuestionnaire(medplum);
  if (questionnaire) {
    const organizationReference = getOrganizationReference(practiceId);
    const existingResponse = await fetchProcedureFeeQuestionnaireResponse(
      medplum,
      code,
      questionnaire,
      undefined,
      organizationReference,
    );

    if (existingResponse?.id) {
      await medplum.deleteResource("QuestionnaireResponse", existingResponse.id);
    }
  }

  // Remove the concept
  codeSystem.concept = codeSystem.concept.filter((conceptItem) => conceptItem.code !== code);

  if (codeSystem.concept.length === 0) {
    await retireEmptyCustomCodeSystemWhenListCleared(medplum, codeSystem);
    const valueSetUrl = getCustomCodeValueSetUrl(practiceId);
    const valueSet = await medplum.searchOne("ValueSet", {
      url: valueSetUrl,
      _compartment: organizationReference,
    });
    if (valueSet) {
      await retireCustomCodeValueSetWhenListCleared(medplum, valueSet);
    }
  } else {
    ensureCustomTagOnCodeSystem(codeSystem);
    await medplum.updateResource(codeSystem);
  }

  // Invalidate searches
  medplum.invalidateSearches("CodeSystem");
  medplum.invalidateSearches("ValueSet");
  medplum.invalidateSearches("QuestionnaireResponse");
  medplum.invalidateSearches("ChargeItemDefinition");

  scheduleCombinedProcedureCodesValueSetRebuild(
    medplum,
    practiceId,
    notifyCombinedProcedureCodesRebuildFailed,
  );
}

/**
 * Creates or updates the practice's ValueSet for custom codes
 */
async function upsertCustomCodeValueSet(
  medplum: MedplumClient,
  practiceId: string,
  codeSystemUrl: string,
): Promise<void> {
  const valueSetUrl = getCustomCodeValueSetUrl(practiceId);
  const organizationReference = getOrganizationReference(practiceId);

  const existingValueSet = await medplum.searchOne("ValueSet", {
    url: valueSetUrl,
    _compartment: organizationReference,
  });

  const valueSet: ValueSet = {
    resourceType: "ValueSet",
    url: valueSetUrl,
    name: "Custom Procedure Codes",
    title: "Custom Procedure Codes",
    status: "active",
    compose: {
      include: [
        {
          system: codeSystemUrl,
        },
      ],
    },
    meta: {
      tag: [CUSTOM_TAG],
    },
  };

  let customValueSet: ValueSet;
  if (existingValueSet?.id) {
    customValueSet = await medplum.updateResource({ ...valueSet, id: existingValueSet.id });
  } else {
    customValueSet = await medplum.createResource(valueSet);
  }

  await setAccountsBestEffort(medplum, customValueSet, [{ reference: organizationReference }]);
}

/**
 * Normalizes NumberInput value to match Zod schema (number | null)
 * Converts empty strings and undefined to null, preserves numbers, converts strings to numbers
 */
export function normalizeNumberInputValue(value: string | number | undefined): number | null {
  if (value === "" || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return value;
  }
  return Number(value) || null;
}
